import { TERMINAL_COMMAND_MAX_BYTES, type TerminalOutputChunk } from "@/lib/terminal";
import type { TerminalTab, TerminalTabsState } from "@/lib/terminal-tabs";

const TERMINAL_TABS_STORAGE_KEY = "izanagi-terminal-tabs";
const LEGACY_DRAFT_STORAGE_KEY = "izanagi-terminal-draft";
const LEGACY_TRANSCRIPT_STORAGE_KEY = "izanagi-terminal-transcript";
const TERMINAL_TABS_VERSION = 2;

export const TERMINAL_TRANSCRIPT_MAX_LINES = 5000;
export const TERMINAL_TRANSCRIPT_MAX_BYTES = 2 * 1024 * 1024;

const encoder = new TextEncoder();

export interface TerminalTranscriptEntry {
	cwd: string | null;
	command: string;
	chunks: TerminalOutputChunk[];
}

export interface TerminalTabRecord extends TerminalTab {
	draft: string;
	entries: (TerminalTranscriptEntry & { seq: number })[];
}

export interface TerminalTabsRecord {
	version: typeof TERMINAL_TABS_VERSION;
	userId: string;
	environmentId: string;
	chatKey: string;
	activeTerminalId: string;
	nextSeq: number;
	tabs: TerminalTabRecord[];
}

function resolveSessionStorage(): Storage | null {
	try {
		return typeof window === "undefined" ? null : window.sessionStorage;
	} catch {
		return null;
	}
}

function remove(storage: Storage, key: string): void {
	try {
		storage.removeItem(key);
	} catch {
		// sessionStorage が利用できなくても、ターミナル自体は通常どおり利用可能にする。
	}
}

function removeLegacyRecords(storage: Storage): void {
	remove(storage, LEGACY_DRAFT_STORAGE_KEY);
	remove(storage, LEGACY_TRANSCRIPT_STORAGE_KEY);
}

function chatKey(chatId: string | null): string {
	return chatId ?? "new";
}

function matchesIdentity(
	record: TerminalTabsRecord,
	userId: string,
	environmentId: string,
	requestedChatKey: string,
): boolean {
	return (
		record.userId === userId &&
		record.environmentId === environmentId &&
		record.chatKey === requestedChatKey
	);
}

function isTerminalOutputChunk(value: unknown): value is TerminalOutputChunk {
	if (typeof value !== "object" || value === null) return false;
	const chunk = value as Partial<TerminalOutputChunk>;
	return (
		typeof chunk.text === "string" &&
		(chunk.color === "default" || chunk.color === "error" || chunk.color === "muted")
	);
}

function isTerminalTranscriptEntry(value: unknown): value is TerminalTranscriptEntry {
	if (typeof value !== "object" || value === null) return false;
	const entry = value as Partial<TerminalTranscriptEntry>;
	return (
		(entry.cwd === null || typeof entry.cwd === "string") &&
		typeof entry.command === "string" &&
		entry.command.length > 0 &&
		encoder.encode(entry.command).byteLength <= TERMINAL_COMMAND_MAX_BYTES &&
		Array.isArray(entry.chunks) &&
		entry.chunks.every(isTerminalOutputChunk)
	);
}

function isTerminalTabRecord(value: unknown): value is TerminalTabRecord {
	if (typeof value !== "object" || value === null) return false;
	const tab = value as Partial<TerminalTabRecord>;
	return (
		typeof tab.terminalId === "string" &&
		tab.terminalId.length > 0 &&
		typeof tab.draft === "string" &&
		encoder.encode(tab.draft).byteLength <= TERMINAL_COMMAND_MAX_BYTES &&
		Array.isArray(tab.entries) &&
		tab.entries.every(
			(entry) =>
				isTerminalTranscriptEntry(entry) && Number.isSafeInteger(entry.seq) && entry.seq >= 0,
		)
	);
}

function isTerminalTabsRecord(value: unknown): value is TerminalTabsRecord {
	if (typeof value !== "object" || value === null) return false;
	const record = value as Partial<TerminalTabsRecord>;
	return (
		record.version === TERMINAL_TABS_VERSION &&
		typeof record.userId === "string" &&
		typeof record.environmentId === "string" &&
		typeof record.chatKey === "string" &&
		typeof record.activeTerminalId === "string" &&
		typeof record.nextSeq === "number" &&
		Number.isSafeInteger(record.nextSeq) &&
		record.nextSeq >= 0 &&
		Array.isArray(record.tabs) &&
		record.tabs.every(isTerminalTabRecord)
	);
}

function logicalLineCount(text: string): number {
	if (text.length === 0) return 0;
	let lines = 0;
	for (const character of text) {
		if (character === "\n") lines++;
	}
	return lines + (text.endsWith("\n") ? 0 : 1);
}

function transcriptEntryLineCount(entry: TerminalTranscriptEntry): number {
	let lines = logicalLineCount(entry.command);
	for (const chunk of entry.chunks) lines += logicalLineCount(chunk.text);
	return lines;
}

function transcriptLineCount(record: TerminalTabsRecord): number {
	let lines = 0;
	for (const tab of record.tabs) {
		for (const entry of tab.entries) lines += transcriptEntryLineCount(entry);
	}
	return lines;
}

function readRecord(storage: Storage): TerminalTabsRecord | null {
	removeLegacyRecords(storage);
	try {
		const raw = storage.getItem(TERMINAL_TABS_STORAGE_KEY);
		if (raw === null) return null;
		if (encoder.encode(raw).byteLength > TERMINAL_TRANSCRIPT_MAX_BYTES) {
			remove(storage, TERMINAL_TABS_STORAGE_KEY);
			return null;
		}
		const record: unknown = JSON.parse(raw);
		if (
			!isTerminalTabsRecord(record) ||
			transcriptLineCount(record) > TERMINAL_TRANSCRIPT_MAX_LINES
		) {
			remove(storage, TERMINAL_TABS_STORAGE_KEY);
			return null;
		}
		return record;
	} catch {
		remove(storage, TERMINAL_TABS_STORAGE_KEY);
		return null;
	}
}

function writeRecord(storage: Storage, record: TerminalTabsRecord): void {
	removeLegacyRecords(storage);
	try {
		storage.setItem(TERMINAL_TABS_STORAGE_KEY, JSON.stringify(record));
	} catch {
		remove(storage, TERMINAL_TABS_STORAGE_KEY);
	}
}

/** 指定コンテキストのタブ保存状態を返す。別コンテキストのレコードは消さない。 */
export function readTerminalTabs(
	userId: string | null | undefined,
	environmentId: string | null | undefined,
	chatId: string | null,
	storage: Storage | null = resolveSessionStorage(),
): TerminalTabsRecord | null {
	if (!userId || !environmentId || !storage) return null;
	const record = readRecord(storage);
	if (record === null || !matchesIdentity(record, userId, environmentId, chatKey(chatId)))
		return null;
	return record;
}

function recordFromTabs(
	userId: string,
	environmentId: string,
	requestedChatKey: string,
	state: TerminalTabsState,
	previous: TerminalTabsRecord | null,
): TerminalTabsRecord {
	const existingTabs = new Map(previous?.tabs.map((tab) => [tab.terminalId, tab]) ?? []);
	return {
		version: TERMINAL_TABS_VERSION,
		userId,
		environmentId,
		chatKey: requestedChatKey,
		activeTerminalId: state.activeTerminalId,
		nextSeq: previous?.nextSeq ?? 0,
		tabs: state.tabs.map((tab) => {
			const existing = existingTabs.get(tab.terminalId);
			return {
				terminalId: tab.terminalId,
				draft: existing?.draft ?? "",
				entries: existing?.entries ?? [],
			};
		}),
	};
}

/**
 * タブ集合を保存する。タブから外れた ID の下書き・履歴は同時に破棄する。
 */
export function writeTerminalTabList(
	userId: string,
	environmentId: string,
	chatId: string | null,
	state: TerminalTabsState,
	storage: Storage | null = resolveSessionStorage(),
): void {
	if (!storage) return;
	const previous = readRecord(storage);
	const requestedChatKey = chatKey(chatId);
	const matchingPrevious =
		previous !== null && matchesIdentity(previous, userId, environmentId, requestedChatKey)
			? previous
			: null;
	writeRecord(
		storage,
		recordFromTabs(userId, environmentId, requestedChatKey, state, matchingPrevious),
	);
}

/** 下書きから実チャットへ昇格したとき、同じタブ集合の identity だけを書き換える。 */
export function rewriteTerminalTabsChatKey(
	userId: string,
	environmentId: string,
	fromChatId: string | null,
	toChatId: string | null,
	storage: Storage | null = resolveSessionStorage(),
): void {
	if (!storage) return;
	const record = readRecord(storage);
	if (record === null || !matchesIdentity(record, userId, environmentId, chatKey(fromChatId)))
		return;
	record.chatKey = chatKey(toChatId);
	writeRecord(storage, record);
}

function withMatchingRecord(
	userId: string,
	environmentId: string,
	chatId: string | null,
	storage: Storage | null,
	mutate: (record: TerminalTabsRecord) => void,
): void {
	if (!storage) return;
	const record = readRecord(storage);
	if (record === null || !matchesIdentity(record, userId, environmentId, chatKey(chatId))) return;
	mutate(record);
	writeRecord(storage, record);
}

export function readTerminalDraft(
	userId: string | null | undefined,
	environmentId: string | null | undefined,
	chatId: string | null,
	terminalId: string,
	storage: Storage | null = resolveSessionStorage(),
): string {
	const record = readTerminalTabs(userId, environmentId, chatId, storage);
	return record?.tabs.find((tab) => tab.terminalId === terminalId)?.draft ?? "";
}

export function writeTerminalDraft(
	userId: string,
	environmentId: string,
	chatId: string | null,
	terminalId: string,
	text: string,
	storage: Storage | null = resolveSessionStorage(),
): void {
	withMatchingRecord(userId, environmentId, chatId, storage, (record) => {
		const tab = record.tabs.find((candidate) => candidate.terminalId === terminalId);
		if (tab === undefined) return;
		tab.draft =
			text.length > 0 && encoder.encode(text).byteLength <= TERMINAL_COMMAND_MAX_BYTES ? text : "";
	});
}

export function clearTerminalDraft(
	userId: string,
	environmentId: string,
	chatId: string | null,
	terminalId: string,
	storage: Storage | null = resolveSessionStorage(),
): void {
	writeTerminalDraft(userId, environmentId, chatId, terminalId, "", storage);
}

export function readTerminalTranscript(
	userId: string | null | undefined,
	environmentId: string | null | undefined,
	chatId: string | null,
	terminalId: string,
	storage: Storage | null = resolveSessionStorage(),
): TerminalTranscriptEntry[] {
	const record = readTerminalTabs(userId, environmentId, chatId, storage);
	const entries = record?.tabs.find((tab) => tab.terminalId === terminalId)?.entries;
	return entries?.map(({ cwd, command, chunks }) => ({ cwd, command, chunks })) ?? [];
}

interface EvictionCandidate {
	tab: TerminalTabRecord;
	entry: TerminalTranscriptEntry & { seq: number };
	index: number;
	lines: number;
	bytes: number;
}

/** 合計上限を超えた履歴を、タブ横断で最古の seq から全実行単位ごとに破棄する。 */
function evictTranscript(record: TerminalTabsRecord): void {
	let totalLines = transcriptLineCount(record);
	let totalBytes = encoder.encode(JSON.stringify(record)).byteLength;
	if (totalLines <= TERMINAL_TRANSCRIPT_MAX_LINES && totalBytes <= TERMINAL_TRANSCRIPT_MAX_BYTES)
		return;

	const candidates: EvictionCandidate[] = [];
	for (const tab of record.tabs) {
		for (const [index, entry] of tab.entries.entries()) {
			candidates.push({
				tab,
				entry,
				index,
				lines: transcriptEntryLineCount(entry),
				bytes: encoder.encode(JSON.stringify(entry)).byteLength,
			});
		}
	}
	candidates.sort((left, right) => left.entry.seq - right.entry.seq);
	const removedPerTab = new Map<TerminalTabRecord, number>();
	const remainingPerTab = new Map(record.tabs.map((tab) => [tab, tab.entries.length]));

	for (const candidate of candidates) {
		if (totalLines <= TERMINAL_TRANSCRIPT_MAX_LINES && totalBytes <= TERMINAL_TRANSCRIPT_MAX_BYTES)
			break;
		const removed = removedPerTab.get(candidate.tab) ?? 0;
		candidate.tab.entries.splice(candidate.index - removed, 1);
		removedPerTab.set(candidate.tab, removed + 1);
		const remaining = remainingPerTab.get(candidate.tab) ?? 0;
		totalLines -= candidate.lines;
		totalBytes -= candidate.bytes + (remaining > 1 ? 1 : 0);
		remainingPerTab.set(candidate.tab, remaining - 1);
	}
}

export function appendTerminalTranscript(
	userId: string,
	environmentId: string,
	chatId: string | null,
	terminalId: string,
	entry: TerminalTranscriptEntry,
	storage: Storage | null = resolveSessionStorage(),
): void {
	if (!isTerminalTranscriptEntry(entry)) return;
	withMatchingRecord(userId, environmentId, chatId, storage, (record) => {
		const tab = record.tabs.find((candidate) => candidate.terminalId === terminalId);
		if (tab === undefined) return;
		tab.entries.push({ ...entry, chunks: [...entry.chunks], seq: record.nextSeq++ });
		evictTranscript(record);
	});
}

export function clearTerminalTranscript(
	userId: string,
	environmentId: string,
	chatId: string | null,
	terminalId: string,
	storage: Storage | null = resolveSessionStorage(),
): void {
	withMatchingRecord(userId, environmentId, chatId, storage, (record) => {
		const tab = record.tabs.find((candidate) => candidate.terminalId === terminalId);
		if (tab !== undefined) tab.entries = [];
	});
}

export function clearTerminalTabs(storage: Storage | null = resolveSessionStorage()): void {
	if (!storage) return;
	removeLegacyRecords(storage);
	remove(storage, TERMINAL_TABS_STORAGE_KEY);
}
